import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Reminder } from "@hub/shared";
import { Button, Field, Input, Modal, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import s from "../shared.module.css";

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function ReminderEditor({ open, onClose, contactId, reminder }: { open: boolean; onClose: () => void; contactId?: string | null; reminder?: Reminder | null }) {
  const settings = useEngineQuery("getUserSettings", undefined);
  const initial = reminder ? new Date(reminder.fireAt) : null;
  const [title, setTitle] = useState(reminder?.title ?? "");
  const [date, setDate] = useState(initial ? ymd(initial) : ymd(new Date(Date.now() + 86_400_000)));
  const [time, setTime] = useState(initial ? `${String(initial.getHours()).padStart(2, "0")}:${String(initial.getMinutes()).padStart(2, "0")}` : settings.data?.defaultReminderTime ?? "09:00");
  const [month, setMonth] = useState(() => { const d = initial ?? new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const save = useEngineMutation("saveReminder");

  useEffect(() => {
    if (open) {
      const d = reminder ? new Date(reminder.fireAt) : null;
      setTitle(reminder?.title ?? "");
      setDate(d ? ymd(d) : ymd(new Date(Date.now() + 86_400_000)));
      setTime(d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : settings.data?.defaultReminderTime ?? "09:00");
    }
  }, [open, reminder, settings.data?.defaultReminderTime]);

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [month]);

  const quick = (days: number, label: string) => ({ label, onClick: () => setDate(ymd(new Date(Date.now() + days * 86_400_000))) });
  const quicks = [quick(1, "Tomorrow"), quick(7, "In a week"), quick(30, "In a month")];

  const submit = async () => {
    const fireAt = new Date(`${date}T${time || "09:00"}:00`).toISOString();
    await save.mutateAsync({ reminderId: reminder?.id, contactId: contactId ?? reminder?.contactId ?? null, title: title.trim() || null, fireAt });
    toast(reminder ? "Reminder updated" : "Reminder set", "success");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={reminder ? "Edit reminder" : "New reminder"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>{reminder ? "Save" : "Set reminder"}</Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }}>
        <Field label="What">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Follow up about the deck" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && submit()} />
        </Field>
        <div className="u-row" style={{ gap: 6 }}>
          {quicks.map((q) => (
            <Button key={q.label} size="sm" variant="outline" onClick={q.onClick}>{q.label}</Button>
          ))}
        </div>
        <div className="u-row" style={{ alignItems: "flex-start", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
              <Button size="sm" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month"><ChevronLeft size={14} /></Button>
              <strong>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</strong>
              <Button size="sm" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month"><ChevronRight size={14} /></Button>
            </div>
            <div className={s.calendarGrid}>
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="u-small u-muted" style={{ textAlign: "center" }}>{d}</div>)}
              {days.map((d) => (
                <button key={d.toISOString()} type="button" className={s.calendarDay} data-active={ymd(d) === date ? "true" : undefined} data-muted={d.getMonth() !== month.getMonth() ? "true" : undefined} onClick={() => setDate(ymd(d))}>
                  {d.getDate()}
                </button>
              ))}
            </div>
          </div>
          <div style={{ width: 150 }} className="u-col">
            <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
            <Field label="Time"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          </div>
        </div>
      </div>
    </Modal>
  );
}
